import toast, { ToastOptions } from 'react-hot-toast'

export const TOAST_DURATION = 2000

const defaultOptions: ToastOptions = {
  duration: TOAST_DURATION,
  position: 'bottom-center',
  style: {
    borderRadius: '999px',
    background: 'rgba(75, 54, 33, 0.8)',
    color: '#ffffff',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    maxWidth: '340px',
    wordBreak: 'keep-all',
  },
}

/**
 * 중복 방지용 토스트 id. 같은 (종류 + 메시지)는 같은 id를 갖게 해서
 * react-hot-toast가 이미 떠 있는 토스트를 갱신만 하고 새로 띄우지 않게 한다.
 * (예: 소셜 로그인 취소 메시지가 두 경로에서 호출돼도 한 번만 표시)
 * 호출 측에서 options.id를 명시하면 그 값을 우선한다.
 */
const makeToastId = (type: string, message: string): string =>
  `${type}:${message}`

const InfoIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="flex-shrink-0"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
)

const WarningIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="flex-shrink-0"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
)

export const toastUtil = {
  success: (message: string, options?: ToastOptions) => {
    return toast(
      (t) => (
        <div className="flex items-center gap-10px">
          <img
            src="/images/toast-success.png"
            alt="성공"
            className="w-24px h-24px flex-shrink-0"
          />
          <span className="text-style-button1 text-textColorText1W">
            {message}
          </span>
        </div>
      ),
      {
        ...defaultOptions,
        ...options,
        id: options?.id ?? makeToastId('success', message),
      }
    )
  },

  error: (message: string, options?: ToastOptions) => {
    return toast(
      (t) => (
        <div className="flex items-center gap-10px">
          <img
            src="/images/toast-error.png"
            alt="오류"
            className="w-24px h-24px flex-shrink-0"
          />
          <span className="font-pretendard font-700 text-16px text-textColorText1W leading-24px">
            {message}
          </span>
        </div>
      ),
      {
        ...defaultOptions,
        ...options,
        id: options?.id ?? makeToastId('error', message),
      }
    )
  },

  info: (message: string, options?: ToastOptions) => {
    return toast(
      (t) => (
        <div className="flex items-center gap-10px">
          <InfoIcon />
          <span className="text-style-button1 text-textColorText1W">
            {message}
          </span>
        </div>
      ),
      {
        ...defaultOptions,
        ...options,
        id: options?.id ?? makeToastId('info', message),
      }
    )
  },

  warning: (message: string, options?: ToastOptions) => {
    return toast(
      (t) => (
        <div className="flex items-center gap-10px">
          <WarningIcon />
          <span className="text-style-button1 text-textColorText1W">
            {message}
          </span>
        </div>
      ),
      {
        ...defaultOptions,
        ...options,
        id: options?.id ?? makeToastId('warning', message),
      }
    )
  },

  loading: (message: string, options?: ToastOptions) => {
    return toast.loading(message, {
      ...defaultOptions,
      ...options,
      id: options?.id ?? makeToastId('loading', message),
    })
  },

  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: any) => string)
    },
    options?: ToastOptions
  ) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading,
        success: messages.success,
        error: messages.error,
      },
      {
        ...defaultOptions,
        ...options,
      }
    )
  },

  dismiss: (toastId?: string) => {
    toast.dismiss(toastId)
  },
}

export default toastUtil
